import { useCallback, useRef, useEffect, useContext } from "react";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import UserContext from "../assets/user_context";
import { useAuth } from "../services/use-auth";

/**
 * Decoupled analytics hook for onboarding tour.
 * Writes analytics data to users/{uid}.tourAnalytics in Firestore.
 * Uses arrayUnion for step tracking to prevent overwrites on rapid transitions.
 */
export function useTourAnalytics() {
  const { user } = useContext(UserContext);
  const { db } = useAuth();

  const userRef = useRef(user);
  const dbRef = useRef(db);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { dbRef.current = db; }, [db]);

  const trackStarted = useCallback(async () => {
    const currentUser = userRef.current;
    const currentDb = dbRef.current;
    if (!currentUser?.uid || !currentDb) return;
    try {
      await updateDoc(doc(currentDb, "users", currentUser.uid), {
        'tourAnalytics.startedAt': new Date().toISOString(),
      });
    } catch (err) {
      console.warn("Tour analytics: failed to track started", err);
    }
  }, []);

  const trackCompleted = useCallback(async () => {
    const currentUser = userRef.current;
    const currentDb = dbRef.current;
    if (!currentUser?.uid || !currentDb) return;
    try {
      await updateDoc(doc(currentDb, "users", currentUser.uid), {
        'tourAnalytics.completedAt': new Date().toISOString(),
      });
    } catch (err) {
      console.warn("Tour analytics: failed to track completed", err);
    }
  }, []);

  const trackDismissed = useCallback(async (stepId, stepIndex, orgId) => {
    const currentUser = userRef.current;
    const currentDb = dbRef.current;
    if (!currentUser?.uid || !currentDb) return;
    try {
      await updateDoc(doc(currentDb, "users", currentUser.uid), {
        'tourAnalytics.skippedAt': new Date().toISOString(),
        'tourAnalytics.skippedAtStep': stepId,
        'tourAnalytics.skippedAtStepIndex': stepIndex,
        'tourAnalytics.orgId': orgId || null,
      });
    } catch (err) {
      console.warn("Tour analytics: failed to track dismissed", err);
    }
  }, []);

  const trackStepViewed = useCallback(async (stepId, stepIndex) => {
    const currentUser = userRef.current;
    const currentDb = dbRef.current;
    if (!currentUser?.uid || !currentDb) return;
    try {
      await updateDoc(doc(currentDb, "users", currentUser.uid), {
        'tourAnalytics.stepsViewed': arrayUnion({
          stepId,
          stepIndex,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (err) {
      console.warn("Tour analytics: failed to track step viewed", err);
    }
  }, []);

  return { trackStarted, trackCompleted, trackDismissed, trackStepViewed };
}
